// Client-side decryption for LEGACY "WAT-ENC v1" portable files.
// Read-only: we still EXPORT the current wat.encrypted format only. This exists
// purely so conversations saved by an older build can be reopened.
//
// Legacy container shape:
//   { magic: "WAT-ENC", v: 1,
//     kdf: { name: "PBKDF2", hash: "SHA-256", iterations: 250000, salt: <b64> },
//     cipher: { name: "AES-GCM", iv: <b64> },
//     ct: <b64 ciphertext> }
//
// PBKDF2-SHA256 derives an AES-256-GCM key. Salt 16 bytes, IV 12 bytes. GCM
// additional authenticated data binds the header exactly as the old writer did.
// The passphrase is NEVER stored, logged, or transmitted.

import { WatError } from "./watCrypto";

const ENC = new TextEncoder();
const DEC = new TextDecoder();
const unb64 = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));

async function deriveKey(passphrase, salt, iterations) {
  const base = await crypto.subtle.importKey(
    "raw",
    ENC.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );
}

const legacyAad = (h) =>
  ENC.encode(
    JSON.stringify({
      magic: h.magic,
      v: h.v,
      kdf: {
        name: h.kdf.name,
        hash: h.kdf.hash,
        iterations: h.kdf.iterations,
        salt: h.kdf.salt,
      },
      cipher: {
        name: h.cipher.name,
        iv: h.cipher.iv,
      },
    })
  );

// Cheap sniff so the importer can route to the legacy path without guessing on
// a wrong passphrase.
export function isLegacyWat(fileText) {
  try {
    const c = JSON.parse(fileText);
    return !!c && c.magic === "WAT-ENC" && Number(c.v) === 1;
  } catch {
    return false;
  }
}

export async function decryptLegacyWat(fileText, passphrase) {
  let c;
  try {
    c = JSON.parse(fileText);
  } catch {
    throw new WatError("This isn't a valid .wat file.");
  }
  if (!c || c.magic !== "WAT-ENC") throw new WatError("Unrecognized file format.");
  if (Number(c.v) !== 1) throw new WatError("This legacy .wat file uses an unsupported version.");
  if (!c.kdf || !c.cipher) throw new WatError("This .wat file is damaged. Nothing was imported.");
  let salt;
  let iv;
  let ctBytes;
  try {
    salt = unb64(c.kdf.salt);
    iv = unb64(c.cipher.iv);
    ctBytes = unb64(c.ct);
  } catch {
    throw new WatError("This .wat file is damaged. Nothing was imported.");
  }
  const iterations = Number(c.kdf.iterations) || 250000;
  const key = await deriveKey(passphrase, salt, iterations);
  let pt;
  try {
    pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, additionalData: legacyAad(c) },
      key,
      ctBytes
    );
  } catch {
    throw new WatError("Wrong passphrase, or the file is damaged. Nothing was imported.");
  }
  try {
    return JSON.parse(DEC.decode(pt));
  } catch {
    throw new WatError("The decrypted data was unreadable. Nothing was imported.");
  }
}
